import React from 'react';
import { Badge } from '../ui/Badge';
import { Eye, Globe, Shield, AlertTriangle, AlertOctagon, AlertCircle } from 'lucide-react';
import { AssetActions } from './AssetActions';
import { TechnologyStack } from './TechnologyStack';
import * as Tooltip from '@radix-ui/react-tooltip';
import { TablePagination } from '../ui/TablePagination';
import { Pagination } from '../ui/Pagination';
import { Link, useLocation } from 'react-router-dom';

interface TlsCertificate {
  sni?: string;
  host?: string;
  port?: string;
  cipher?: string;
  issuer_cn?: string;
  not_after?: string;
  not_before?: string;
  subject_cn?: string;
  tls_version?: string;
  probe_status?: boolean;
}

interface Domain {
  id: string;
  name: string;
  riskScore: string;
  updatedAt: string;
  tags: string[];
  vulnerabilities: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  registrar?: string;
  registrationDate?: string | null;
  expirationDate?: string | null;
  tlsCertificate?: TlsCertificate | null;
  subdomains?: {
    total: number;
    active: number;
    inactive: number;
  };
  technologies?: string | string[];
}

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface DomainsTableProps {
  domains: Domain[];
  currentPage: number;
  onPageChange: (page: number) => void;
  totalDomains: number;
  itemsPerPage: number;
  paginationInfo: PaginationInfo;
}

export const DomainsTable: React.FC<DomainsTableProps> = ({
  domains,
  currentPage,
  onPageChange,
  totalDomains,
  itemsPerPage,
  paginationInfo,
}) => {
  const getRiskColor = (score: string) => {
    switch (score) {
      case "A+": return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
      case "A": return 'text-green-500 bg-green-500/10 border-green-500/20';
      case "B": return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
      case "C": return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case "F": return 'text-red-500 bg-red-500/10 border-red-500/20';
      default: return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
    }
  };

  const getVulnerabilityIcon = (count: number, type: 'critical' | 'high' | 'medium' | 'low') => {
    if (count === 0) return null;

    const icons = {
      critical: { icon: AlertOctagon, color: 'text-red-500' },
      high: { icon: AlertTriangle, color: 'text-orange-500' },
      medium: { icon: AlertCircle, color: 'text-yellow-500' },
      low: { icon: Shield, color: 'text-emerald-500' }
    };

    const { icon: Icon, color } = icons[type];
    
    return (
      <Tooltip.Provider>
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <div className={`flex items-center space-x-1 ${color}`}>
              <Icon className="h-4 w-4" />
              <span className="text-xs font-medium">{count}</span>
            </div>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
              sideOffset={5}
            >
              {count} {type} vulnerabilities
              <Tooltip.Arrow className="fill-[#1A1F2E]" />
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </Tooltip.Provider>
    );
  };

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalDomains);

  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden border border-gray-800/50 flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Domain</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Vulnerabilities</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Technologies</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Risk Score</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Domain Details</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Subdomains</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Changes Detected</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {domains.map((domain) => (
              <tr key={domain.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-gray-800/30 group-hover:bg-gray-800/50 transition-colors">
                      <Globe className="h-5 w-5 text-gray-400" />
                    </div>
                    <div>
                      <Link to={`/domains/${domain.id}`} className="text-sm font-medium text-white">{domain.name}</Link>
                      <div className="flex items-center space-x-2 mt-1 flex-wrap">
                        {domain.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 text-xs rounded-full bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-300 transition-colors cursor-pointer"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {domain.vulnerabilities && 
                   (domain.vulnerabilities.critical > 0 || 
                    domain.vulnerabilities.high > 0 || 
                    domain.vulnerabilities.medium > 0 || 
                    domain.vulnerabilities.low > 0) ? (
                    <div className="flex items-center space-x-2">
                      {getVulnerabilityIcon(domain.vulnerabilities?.critical, 'critical')}
                      {getVulnerabilityIcon(domain.vulnerabilities?.high, 'high')}
                      {getVulnerabilityIcon(domain.vulnerabilities?.medium, 'medium')}
                      {getVulnerabilityIcon(domain.vulnerabilities?.low, 'low')}
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span className="px-2 py-1 text-xs rounded-md bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        No vulnerabilities
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {domain.technologies && Array.isArray(domain.technologies) && domain.technologies.length > 0 || 
                   (typeof domain.technologies === 'string' && domain.technologies) ? (
                    <TechnologyStack
                      technologies={
                        domain.technologies 
                          ? (typeof domain.technologies === 'string' 
                              ? (
                                  // Try to parse as JSON if it's a string
                                  (() => {
                                    try {
                                      const parsed = JSON.parse(domain.technologies);
                                      return Array.isArray(parsed) ? parsed : [];
                                    } catch (e) {
                                      // If parsing fails, return empty array
                                      return [];
                                    }
                                  })()
                                ) 
                              : domain.technologies
                            )
                          : []
                      }
                      maxVisible={3}
                    />
                  ) : (
                    <div className="flex items-center">
                      <span className="px-2 py-1 text-xs rounded-md bg-gray-800/50 text-gray-400 border border-gray-700/30">
                        No technologies detected
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <Badge className={getRiskColor(domain.riskScore?.toUpperCase() || 'Unknown')}>
                    {domain.riskScore?.toUpperCase() || 'Unknown'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-2">
                    {domain.tlsCertificate && (
                      <Badge
                        className={`${domain.tlsCertificate.probe_status ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}
                      >
                        <div className="flex items-center space-x-1 w-max">
                          <Shield className="h-3 w-3" />
                          <span>{domain.tlsCertificate.probe_status ? 'TLS Valid' : 'TLS Invalid'}</span>
                        </div>
                      </Badge>
                    )}
                    {domain.expirationDate && (
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                        <div className="flex items-center space-x-1 w-max">
                          <span>Expires: {new Date(domain.expirationDate).toLocaleDateString()}</span>
                        </div>
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-2">
                    <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                      <div className="flex items-center space-x-1 w-max">
                        <span>Total: {domain.subdomains?.total || 0}</span>
                      </div>
                    </Badge>
                    <div className="flex items-center space-x-2 w-max">
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                        <span>Active: {domain.subdomains?.active || 0}</span>
                      </Badge>
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/20">
                        <span>Inactive: {domain.subdomains?.inactive || 0}</span>
                      </Badge>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <Tooltip.Provider>
                    <Tooltip.Root>
                      <Tooltip.Trigger asChild>
                        <div className="text-sm text-gray-300 cursor-help">
                          {domain.updatedAt?.split(",").map((item: string) => (
                            <span key={item}>{item}</span>
                          ))}
                        </div>
                      </Tooltip.Trigger>
                      <Tooltip.Portal>
                        <Tooltip.Content
                          className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
                          sideOffset={5}
                        >
                          <div className="space-y-1">
                            <div>Changes Detected {domain.updatedAt}</div>
                            {domain.registrar && <div>Registrar: {domain.registrar}</div>}
                            {domain.registrationDate && <div>Registered: {domain.registrationDate}</div>}
                          </div>
                          <Tooltip.Arrow className="fill-[#1A1F2E]" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>
                  </Tooltip.Provider>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/domains/${domain.id}`}
                      className="bg-[#1A1F2E] text-white"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    <AssetActions assetId={domain.id} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        onPageChange={onPageChange}
        totalItems={totalDomains}
        itemsPerPage={itemsPerPage}
        paginationInfo={paginationInfo}
        itemName="domains"
      />
    </div>
  );
};
